// F7 / FR-N7: per kind x channel notification preferences (GET/PUT /me/notification-prefs).
// The desktop column is an OS-notification toggle: it is only offered where the OS can show one
// (the desktop app, or a browser with the Notifications API) and turning it on asks the OS for
// permission first — the server holds no permission state, the pref is the client's report.
// A channel the server marks unavailable for a kind (email with no template) shows a dash, not
// a switch that would look on and never send anything.
import { useEffect, useState } from "react";
import { Switch } from "@grackle/ui";
import { ApiFail } from "../api/client";
import { FormNotice } from "../components/form";
import {
  getNotificationPrefs,
  putNotificationPrefs,
  type NotificationChannel,
  type NotificationPref,
} from "./api";
import { getOsPermission, osNotificationsSupported, requestOsPermission, type OsPermission } from "./osNotify";

const KIND_LABEL: Record<string, string> = {
  "recording.ready": "My recording is ready",
  "recording.shared_with_you": "A recording is shared with me",
  "meeting.notes_ready": "Meeting notes are ready",
  "transcript.ready": "A transcript is ready",
  "share_link.view_limit_reached": "A share link reaches its view limit",
  "org.invite": "I'm invited to an organization",
  "recording.retention_scheduled": "A retention policy is about to act on my recording",
  "quota.threshold": "A recording nears or passes its viewing limit",
};

const CHANNELS: { id: NotificationChannel; label: string }[] = [
  { id: "in_app", label: "In app" },
  { id: "email", label: "Email" },
  { id: "desktop", label: "Desktop" },
];

export default function NotificationPrefs() {
  const [prefs, setPrefs] = useState<NotificationPref[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permission, setPermission] = useState<OsPermission>("unsupported");
  const desktopOffered = osNotificationsSupported();

  useEffect(() => {
    getNotificationPrefs()
      .then((res) => setPrefs(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load notification settings."));
    if (desktopOffered) void getOsPermission().then(setPermission);
  }, [desktopOffered]);

  async function setOne(kind: string, channel: NotificationChannel, enabled: boolean) {
    if (!prefs) return;
    setError(null);
    if (channel === "desktop" && enabled && permission !== "granted") {
      const next = await requestOsPermission();
      setPermission(next);
      if (next !== "granted") {
        setError("Your system didn't allow notifications from Grackle. Allow them in system settings, then try again.");
        return;
      }
    }
    const before = prefs;
    setPrefs(prefs.map((p) => (p.kind === kind && p.channel === channel ? { ...p, enabled } : p)));
    try {
      const res = await putNotificationPrefs([{ kind, channel, enabled }]);
      setPrefs(res.items);
    } catch (err) {
      setPrefs(before);
      setError(err instanceof ApiFail ? err.message : "Couldn't save that.");
    }
  }

  if (!prefs) return error ? <FormNotice tone="error">{error}</FormNotice> : <div className="account-section-sub">Loading…</div>;

  const kinds = Array.from(new Set(prefs.map((p) => p.kind)));
  const channels = CHANNELS.filter((c) => c.id !== "desktop" || desktopOffered);

  return (
    <div>
      <div className="account-section-title">Notifications</div>
      <div className="account-section-sub">
        Choose where each kind of update reaches you. Grackle never notifies you about who viewed or commented on a
        recording.
      </div>
      {error ? <FormNotice tone="error">{error}</FormNotice> : null}
      <table className="notif-prefs">
        <thead>
          <tr>
            <th scope="col">Notify me when</th>
            {channels.map((c) => (
              <th scope="col" key={c.id}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {kinds.map((kind) => (
            <tr key={kind}>
              <th scope="row">{KIND_LABEL[kind] ?? kind}</th>
              {channels.map((c) => {
                const pref = prefs.find((p) => p.kind === kind && p.channel === c.id);
                if (pref?.available === false) {
                  return (
                    <td key={c.id}>
                      <span
                        className="notif-prefs__na"
                        role="img"
                        aria-label={`${KIND_LABEL[kind] ?? kind} — not sent by ${c.label.toLowerCase()}`}
                        title={`Not sent by ${c.label.toLowerCase()}`}
                      >
                        —
                      </span>
                    </td>
                  );
                }
                return (
                  <td key={c.id}>
                    <Switch
                      size="sm"
                      checked={pref?.enabled ?? false}
                      label={`${KIND_LABEL[kind] ?? kind} — ${c.label}`}
                      onChange={(v) => void setOne(kind, c.id, v)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
